import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Panel } from "@/components/common/Panel";
import { query } from "@/services/services";
import { formatMoney, fromNow } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QuickAddDialogs } from "@/components/shell/QuickAddDialogs";
import { ImportClientsDialog } from "@/components/clients/ImportClientsDialog";
import { Search, Users } from "lucide-react";

export default function Clients() {
  const { db, can } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const [quick, setQuick] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const types = ["All", ...new Set(db.clients.map((c) => c.type))];
  const rows = useMemo(() => db.clients.filter((c) => {
    const q = search.toLowerCase();
    return (!q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)) && (type === "All" || c.type === type);
  }), [db.clients, search, type]);

  return (
    <div>
      <PageHeader title="Clients" subtitle="Individuals, companies and institutions you represent.">
        {can("client.edit") && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-clients-btn">Import CSV</Button>}
        {can("client.edit") && <Button onClick={() => setQuick(true)} data-testid="new-client-btn">New Client</Button>}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-9" data-testid="clients-search" />
        </div>
        <div className="flex flex-wrap gap-1">{types.map((t) => <button key={t} onClick={() => setType(t)} className={`rounded-full px-3 py-1 text-xs font-medium ${type === t ? "bg-navy text-white" : "border border-line text-ink-2"}`}>{t}</button>)}</div>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Users} title="No clients found" message="Add your first client to start creating matters." actionLabel={can("client.edit") ? "New Client" : undefined} onAction={() => setQuick(true)} />
      ) : (
        <Panel>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-line bg-surface text-left text-xs uppercase tracking-wide text-ink-muted">
              <th className="px-4 py-2.5 font-medium">Client</th><th className="px-4 py-2.5 font-medium">Type</th><th className="px-4 py-2.5 font-medium">Mobile</th><th className="px-4 py-2.5 font-medium">Active Matters</th><th className="px-4 py-2.5 font-medium">Outstanding</th><th className="px-4 py-2.5 font-medium">Relationship Mgr</th><th className="px-4 py-2.5 font-medium">Status</th>
            </tr></thead>
            <tbody>{rows.map((c) => {
              const matters = query.mattersForClient(db, c.id).length;
              return (
                <tr key={c.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-medium text-ink">{c.name}<div className="text-xs font-normal text-ink-muted">{c.email}</div></td>
                  <td className="px-4 py-2.5 text-ink-2">{c.type}</td>
                  <td className="px-4 py-2.5 text-ink-2">{c.mobile}</td>
                  <td className="px-4 py-2.5 text-ink-2">{matters}</td>
                  <td className="px-4 py-2.5"><span className={c.outstanding > 0 ? "font-medium text-danger" : "text-ink-2"}>{formatMoney(c.outstanding)}</span></td>
                  <td className="px-4 py-2.5 text-ink-2">{query.userName(db, c.rm)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        </Panel>
      )}
      {quick && <QuickAddDialogs type="client" onClose={() => setQuick(false)} />}
      <ImportClientsDialog open={importOpen} onClose={() => setImportOpen(false)} />}
    </div>
  );
}
